/**
 * Badge — generic pill UI used across lists, cards, and status indicators.
 *
 * Acts as the rendering surface for the unified event-type palette. The
 * canonical `BadgeColor` union lives in `eventConstants.ts` (alongside the
 * event-type → badge-color map and `getEventTypeBadge`). This file imports
 * it so adding a new event-type bucket means ONE union edit — the prop
 * type, the runtime cast, and the color-config keys all flow from a single
 * source of truth.
 *
 * The prop-level `color` accepts `'danger'` as a back-compat alias for
 * `'error'`. We remap it before indexing into the color-configs, so the
 * internal narrow type is exactly `BadgeColor`.
 */
import React from 'react';
import classNames from 'classnames';
import type { BadgeColor } from '@/utils/constants/eventConstants';

/** Input-only addition: 'danger' is an alias the prop accepts. The
 *  runtime remaps it to 'error' before any color-config lookup. */
type BadgeColorProp = BadgeColor | 'danger';

export interface BadgeProps {
  children: React.ReactNode;
  /** Style variant: filled, outline, or none. Shorthand: success/warning/danger/destructive/default/secondary map to filled+color */
  variant?: 'filled' | 'outline' | 'none' | 'success' | 'warning' | 'danger' | 'destructive' | 'default' | 'secondary' | 'subtle';
  // Color tokens — see `BadgeColor` in `eventConstants.ts`. Unified event-
  // type palette includes 'purple' (Class), 'blue' (Private Lesson),
  // 'orange' (Fundraiser); Gym Closings V1 (U4) adds 'gym_closed' for the
  // neutral-gray closed-day overlay (reused on mobile in lockstep). The
  // 'danger' alias is back-compat for 'error'.
  color?: BadgeColorProp;
  /** @deprecated Use color instead. Kept for back-compat */
  size?: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  title?: string;
}

export function Badge({
  children,
  variant: variantProp = 'filled',
  color: colorProp,
  className,
  onClick,
  title
}: BadgeProps) {
  const isClickable = !!onClick;
  // Shorthand: variant="success" => variant="filled" color="success"
  const shorthandMap: Record<string, BadgeColor> = { success: 'success', warning: 'warning', danger: 'error', destructive: 'error', default: 'light', secondary: 'light', subtle: 'light' };
  const isShorthand = variantProp in shorthandMap;
  const variant = (isShorthand ? 'filled' : variantProp) as 'filled' | 'outline' | 'none';
  const resolvedColor: BadgeColorProp = colorProp ?? (isShorthand ? shorthandMap[variantProp] : 'primary');
  const color: BadgeColor = resolvedColor === 'danger' ? 'error' : resolvedColor;
  
  // A text COLOR class (e.g. `dark:text-brand-300`) must not be mistaken for a
  // text SIZE override: match only font-size utilities (`text-xs`, `text-lg`,
  // `text-[13px]`, `text-[length:…]`) so colored chips keep the default 11px
  // instead of silently inheriting the page's much-larger body font size.
  // The arbitrary-value branch requires a digit, dot, or `length:` so arbitrary
  // COLORS (`text-[#ff0000]`, `text-[var(--c)]`, `text-[color:…]`) are NOT
  // misread as sizes — that would reintroduce the very bug this guards against.
  const hasTextSizeOverride = /\btext-(?:xs|sm|base|lg|[0-9]?xl|\[(?:[0-9.]|length:))/.test(className ?? '');

  const baseStyles = classNames(
    'inline-flex items-center rounded-full font-medium transition-colors gap-1',
    // Default size if not overridden in className
    !className?.includes('px-') && 'px-2.5 py-0.5',
    !hasTextSizeOverride && 'text-[11px] leading-4',
    isClickable && 'cursor-pointer select-none focus:outline-none focus:ring-2 focus:ring-offset-2'
  );
  
  const getColorStyles = () => {
    if (variant === 'none') return '';

    const colorConfigs = {
      filled: {
        primary: {
          base: 'bg-brand-500/10 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300',
          hover: 'hover:bg-brand-500/20',
          ring: 'focus:ring-brand-500'
        },
        success: {
          base: 'bg-success-500/10 text-success-700 dark:bg-success-500/20 dark:text-success-300',
          hover: 'hover:bg-success-500/20',
          ring: 'focus:ring-success-500'
        },
        error: {
          base: 'bg-error-500/10 text-error-700 dark:bg-error-500/20 dark:text-error-300',
          hover: 'hover:bg-error-500/20',
          ring: 'focus:ring-error-500'
        },
        warning: {
          base: 'bg-warning-500/10 text-warning-700 dark:bg-warning-500/20 dark:text-warning-300',
          hover: 'hover:bg-warning-500/20',
          ring: 'focus:ring-warning-500'
        },
        info: {
          base: 'bg-blue-light-500/10 text-blue-light-700 dark:bg-blue-light-500/20 dark:text-blue-light-300',
          hover: 'hover:bg-blue-light-500/20',
          ring: 'focus:ring-blue-light-500'
        },
        light: {
          base: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
          hover: 'hover:bg-gray-200 dark:hover:bg-gray-700',
          ring: 'focus:ring-gray-400'
        },
        dark: {
          base: 'bg-gray-800 text-gray-100 dark:bg-gray-700 dark:text-gray-200',
          hover: 'hover:bg-gray-700 dark:hover:bg-gray-600',
          ring: 'focus:ring-gray-700'
        },
        purple: {
          base: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
          hover: 'hover:bg-purple-200 dark:hover:bg-purple-900/50',
          ring: 'focus:ring-purple-500'
        },
        blue: {
          base: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
          hover: 'hover:bg-blue-200 dark:hover:bg-blue-900/50',
          ring: 'focus:ring-blue-500'
        },
        orange: {
          base: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
          hover: 'hover:bg-orange-200 dark:hover:bg-orange-900/50',
          ring: 'focus:ring-orange-500'
        },
        // U4: neutral-gray bucket for the "gym closed" overlay. WCAG AA
        // verified for both light (gray-100 / gray-700) and dark (gray-800 /
        // gray-200) text pairings in the plan's pre-implementation block.
        gym_closed: {
          base: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200 border border-gray-400 dark:border-gray-500',
          hover: 'hover:bg-gray-200 dark:hover:bg-gray-700',
          ring: 'focus:ring-gray-500'
        }
      },
      outline: {
        primary: {
          base: 'border border-brand-200 text-brand-700 dark:border-brand-700 dark:text-brand-300',
          hover: 'hover:bg-brand-50 dark:hover:bg-brand-900/20',
          ring: 'focus:ring-brand-500'
        },
        success: {
          base: 'border border-success-200 text-success-700 dark:border-success-700 dark:text-success-300',
          hover: 'hover:bg-success-50 dark:hover:bg-success-900/20',
          ring: 'focus:ring-success-500'
        },
        error: {
          base: 'border border-error-200 text-error-700 dark:border-error-700 dark:text-error-300',
          hover: 'hover:bg-error-50 dark:hover:bg-error-900/20',
          ring: 'focus:ring-error-500'
        },
        warning: {
          base: 'border border-warning-200 text-warning-700 dark:border-warning-700 dark:text-warning-300',
          hover: 'hover:bg-warning-50 dark:hover:bg-warning-900/20',
          ring: 'focus:ring-warning-500'
        },
        info: {
          base: 'border border-blue-light-200 text-blue-light-700 dark:border-blue-light-700 dark:text-blue-light-300',
          hover: 'hover:bg-blue-light-50 dark:hover:bg-blue-light-900/20',
          ring: 'focus:ring-blue-light-500'
        },
        light: {
          base: 'border border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-400',
          hover: 'hover:bg-gray-50 dark:hover:bg-gray-800/50',
          ring: 'focus:ring-gray-400'
        },
        dark: {
          base: 'border border-gray-700 text-gray-800 dark:border-gray-600 dark:text-gray-200',
          hover: 'hover:bg-gray-100 dark:hover:bg-gray-800/50',
          ring: 'focus:ring-gray-700'
        },
        purple: {
          base: 'border border-purple-200 text-purple-700 dark:border-purple-700 dark:text-purple-300',
          hover: 'hover:bg-purple-50 dark:hover:bg-purple-900/20',
          ring: 'focus:ring-purple-500'
        },
        blue: {
          base: 'border border-blue-200 text-blue-700 dark:border-blue-700 dark:text-blue-300',
          hover: 'hover:bg-blue-50 dark:hover:bg-blue-900/20',
          ring: 'focus:ring-blue-500'
        },
        orange: {
          base: 'border border-orange-200 text-orange-700 dark:border-orange-700 dark:text-orange-300',
          hover: 'hover:bg-orange-50 dark:hover:bg-orange-900/20',
          ring: 'focus:ring-orange-500'
        },
        // U4 outline variant of the gym_closed bucket — matches the filled
        // variant's gray-400/500 border so the calendar's outlined chip
        // surfaces (e.g. the day band's hover state on mobile) read as
        // intentional neutrals, not a different gray than the filled pill.
        gym_closed: {
          base: 'border border-gray-400 text-gray-700 dark:border-gray-500 dark:text-gray-200',
          hover: 'hover:bg-gray-50 dark:hover:bg-gray-800/50',
          ring: 'focus:ring-gray-500'
        }
      }
    };
    
    const variantStyles = colorConfigs[variant === 'filled' || variant === 'outline' ? variant : 'filled'];
    const colorStyle = variantStyles[color] || variantStyles.primary;
    
    return classNames(
      colorStyle.base,
      isClickable && colorStyle.hover,
      isClickable && colorStyle.ring
    );
  };
  
  if (isClickable) {
    return (
      <button 
        type="button"
        data-badge=""
        className={classNames(baseStyles, getColorStyles(), className)}
        onClick={onClick}
        title={title}
      >
        {children}
      </button>
    );
  }

  return (
    <span data-badge="" className={classNames(baseStyles, getColorStyles(), className)} title={title}>
      {children}
    </span>
  );
}

export default Badge; 
